// BUILD_ID: v7-20260103-2050 - SDK Integration
const BUILD_ID = "v7-20260103-2050";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "wouter";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useWallet } from "@/hooks/use-wallet";
import { useCreatePool } from "@/hooks/use-pools";
import { useMissoutSDK } from "@/hooks/useMissoutSDK";
import { protocolAdapter, TokenInfo, TokenPriceData } from "@/lib/protocolAdapter";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { PublicKey } from "@solana/web3.js";

// Backend DEV wallet authorized for pool operations (unlock, randomness, select_winner, payout)
const DEV_WALLET_PUBKEY = import.meta.env.VITE_DEV_WALLET_PUBKEY || "DCHhAjoVvJ4mUUkbQrsKrPztRhivrNV3fDJEZfHNQ8d3";
const TREASURY_WALLET_PUBKEY = import.meta.env.VITE_TREASURY_WALLET_PUBKEY || "4ZscUyoKFWfU7wjeZKpiuw7Nr8Q8ZdAQmr4YzHNQ74B3";
// Sponsor wallet that can create FREE pools
const SPONSOR_WALLET_PUBKEY = "HeXjPXForQumceDJHA6w5d4vPR11mPMDGmtcz5ZHezBZ";
import { showTransactionToast } from "@/lib/transaction-toast";
import {
  Loader2,
  ArrowLeft,
  ArrowRight,
  Atom,
  Search,
  AlertCircle,
  AlertTriangle,
  Check,
  Coins,
  Users,
  Clock,
  Zap,
  CheckCircle2,
  ShoppingCart,
  Settings2,
  RefreshCw,
  FlaskConical
} from "lucide-react";
import { Link } from "wouter";
import { getJupiterQuote, executeJupiterSwap, formatTokenAmount, JupiterQuote } from "@/lib/jupiterSwap";
import { useConnection } from "@solana/wallet-adapter-react";

type Step = 1 | 2 | 3 | 4;

export default function CreatePool() {
  const [step, setStep] = useState<Step>(1);
  const [mintAddress, setMintAddress] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [priceData, setPriceData] = useState<TokenPriceData | null>(null);
  
  const [participants, setParticipants] = useState(10);
  const [entryAmount, setEntryAmount] = useState<string>("");
  const [lockDuration, setLockDuration] = useState(60);
  const [customDuration, setCustomDuration] = useState("");
  const [isFreePool, setIsFreePool] = useState(false);

  const [solAmount, setSolAmount] = useState<string>("");
  const [slippageBps, setSlippageBps] = useState<number>(100);
  const [jupiterQuote, setJupiterQuote] = useState<JupiterQuote | null>(null);
  const [isQuoting, setIsQuoting] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);
  const [showSlippageSettings, setShowSlippageSettings] = useState(false);

  // AI Safety Analysis
  const [aiAnalysis, setAiAnalysis] = useState<any | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);

  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { isConnected, address, connect } = useWallet();
  const { mutate: createPoolBackend, isPending: isCreating } = useCreatePool();
  const { createPool: createPoolOnChain, sdkReady, publicKey, signTransaction } = useMissoutSDK();
  const { connection } = useConnection();

  const currentPrice = priceData?.priceUsd || 0;
  const entryUsd = parseFloat(entryAmount) * currentPrice;
  // If price is available, enforce $5 minimum. If unavailable, allow proceeding with warning.
  const isValidEntry = priceData?.priceUsd ? entryUsd >= 5 : true;
  const suggestedAmount = priceData?.priceUsd ? protocolAdapter.suggestMinEntryAmount(priceData.priceUsd) : 0;

  const handleFetchToken = async () => {
    if (!protocolAdapter.isValidSolanaAddress(mintAddress)) {
      toast({ variant: "destructive", title: "Invalid Address", description: "Please enter a valid Solana SPL mint address." });
      return;
    }

    setIsFetching(true);
    try {
      const info = await protocolAdapter.fetchTokenInfo(mintAddress);
      const price = await protocolAdapter.fetchTokenPriceUsd(mintAddress);
      setTokenInfo(info);
      setPriceData(price);
      setStep(2);
    } catch (e: any) {
      toast({ variant: "destructive", title: "Fetch Failed", description: e.message });
    } finally {
      setIsFetching(false);
    }
  };

  const handleAnalyzeToken = async () => {
    if (!mintAddress) return;

    setIsAnalyzing(true);
    setShowAnalysis(true);
    
    try {
      const response = await apiFetch('/api/agent/analyze-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mintAddress }),
      });

      const result = await response.json();
      
      if (result.success && result.data) {
        setAiAnalysis(result.data);
        toast({
          title: "AI Analysis Complete",
          description: `Token scored ${result.data.safeScore.toFixed(1)}/100 (${result.data.riskLevel})`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Analysis Failed",
          description: result.error || "Could not analyze token",
        });
      }
    } catch (error: any) {
      console.error('AI Analysis error:', error);
      toast({
        variant: "destructive",
        title: "Analysis Error",
        description: error.message || "Failed to connect to AI agent",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleCreate = async () => {
    if (!isConnected || !address) {
      connect();
      return;
    }

    if (!tokenInfo) {
      return;
    }

    if (!sdkReady || !publicKey) {
      console.error("ABORT: SDK not ready or publicKey null");
      toast({ variant: "destructive", title: "SDK Not Ready", description: "Wallet not fully connected. Please reconnect." });
      return;
    }

    let signature: string | undefined;
    let poolAddress: string | undefined;
    
    try {
      const mintPubkey = new PublicKey(tokenInfo.mint);
      const lockDurationSeconds = lockDuration * 60;

      const sdkResult = await createPoolOnChain({
        mint: mintPubkey,
        amount: entryAmount,
        maxParticipants: participants,
        lockDurationSeconds,
        devWallet: new PublicKey(DEV_WALLET_PUBKEY),
        devFeeBps: 500,      // 5% dev fee
        burnFeeBps: 350,     // 3.5% burn fee
        treasuryWallet: new PublicKey(TREASURY_WALLET_PUBKEY),
        treasuryFeeBps: 150, // 1.5% treasury fee for referral rewards
      });

      signature = sdkResult?.tx;
      poolAddress = sdkResult?.poolId;

      if (signature) {
        showTransactionToast({
          type: "success",
          title: "On-Chain Success",
          description: "Black hole initialized in the void.",
          txHash: signature
        });
      }

    } catch (err: any) {
      console.error("=== SDK_ERROR ===", err);
      showTransactionToast({ 
        type: "error",
        title: "Transaction Failed", 
        description: err.message || "Could not create pool on-chain"
      });
      return;
    }

    if (!signature) {
      console.error("=== GUARD_NO_SIGNATURE ===");
      showTransactionToast({ type: "error", title: "No Signature", description: "Wallet did not return a transaction signature." });
      return;
    }

    if (!poolAddress) {
      console.error("=== GUARD_NO_POOL_PDA ===");
      showTransactionToast({ type: "error", title: "No Pool Address", description: "Could not derive pool address." });
      return;
    }


      createPoolBackend({
        tokenSymbol: tokenInfo.symbol,
        tokenName: tokenInfo.name,
        tokenMint: tokenInfo.mint,
        tokenLogoUrl: tokenInfo.logoUrl || null,
        poolAddress: poolAddress,
        txHash: signature,
        entryAmount: parseFloat(entryAmount),
        minParticipants: 2,
        maxParticipants: participants,
        lockDuration: lockDuration,
        creatorWallet: address,
        ...(isFreePool && {
          isFree: 1,
          sponsoredBy: address,
        }),
      }, {
      onSuccess: (pool) => {
        showTransactionToast({ 
          type: "success",
          title: "Black Hole Initialized", 
          description: "The singularity has been created and synced." 
        });

        setLocation(`/pool/${pool.id}`);
      },
      onError: (err) => {
        console.error("=== BACKEND_ERROR ===", err);
        showTransactionToast({ 
          type: "error",
          title: "Backend Sync Failed", 
          description: "Pool created on-chain but not saved to database." 
        });
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && step === 1 && mintAddress && !isFetching) {
      e.preventDefault();
      handleFetchToken();
    }
  };

  const handleGetQuote = async () => {
    if (!tokenInfo || !solAmount || parseFloat(solAmount) <= 0) return;
    
    setIsQuoting(true);
    setJupiterQuote(null);
    
    try {
      const quote = await getJupiterQuote(tokenInfo.mint, parseFloat(solAmount), slippageBps);
      if (quote) {
        setJupiterQuote(quote);
      } else {
        toast({ variant: "destructive", title: "Quote Failed", description: "Could not get swap quote. Token may not have liquidity." });
      }
    } catch (error: any) {
      toast({ variant: "destructive", title: "Quote Error", description: error.message });
    } finally {
      setIsQuoting(false);
    }
  };

  const handleSwap = async () => {
    if (!jupiterQuote || !publicKey || !signTransaction || !connection) {
      toast({ variant: "destructive", title: "Wallet Required", description: "Please connect your wallet to swap." });
      return;
    }

    setIsSwapping(true);
    try {
      const result = await executeJupiterSwap(
        jupiterQuote,
        publicKey.toBase58(),
        DEV_WALLET_PUBKEY,
        signTransaction,
        connection
      );
      
      showTransactionToast({ 
        type: "success",
        title: "Swap Successful!", 
        description: `Bought ${formatTokenAmount(result.outputAmount, tokenInfo?.decimals || 9)} ${tokenInfo?.symbol}`,
        txHash: result.signature
      });
      
      setJupiterQuote(null);
      setSolAmount("");
    } catch (error: any) {
      console.error("[Jupiter] Swap error:", error);
      showTransactionToast({ 
        type: "error",
        title: "Swap Failed", 
        description: error.message || "Failed to swap SOL for tokens"
      });
    } finally {
      setIsSwapping(false);
    }
  };

  useEffect(() => {
    if (solAmount && parseFloat(solAmount) > 0 && tokenInfo?.mint) {
      const fetchQuote = async () => {
        setIsQuoting(true);
        try {
          const quote = await getJupiterQuote(tokenInfo.mint, parseFloat(solAmount), slippageBps);
          if (quote) {
            setJupiterQuote(quote);
          }
        } catch (error) {
          console.error("[Jupiter] Quote error:", error);
        } finally {
          setIsQuoting(false);
        }
      };
      
      const debounce = setTimeout(fetchQuote, 500);
      return () => clearTimeout(debounce);
    } else {
      setJupiterQuote(null);
    }
  }, [solAmount, slippageBps, tokenInfo?.mint]);

  const canProceedToStep3 = tokenInfo !== null;
  const canProceedToStep4 = entryAmount && parseFloat(entryAmount) > 0 && isValidEntry;

  return (
    <div className="min-h-screen bg-black bg-grid-pattern">
      <main className="container mx-auto px-4 py-12 max-w-2xl">
        <Link href="/terminal" className="inline-flex items-center text-muted-foreground hover:text-primary mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Pool Terminal
        </Link>

        <div className="bg-zinc-900/50 border border-white/10 p-8 rounded-lg backdrop-blur-md">
          <div className="mb-8 border-b border-white/10 pb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Atom className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-3xl font-display font-bold text-white">INITIALIZE BLACK HOLE</h1>
            </div>
            <p className="text-muted-foreground">Configure parameters for a new MissOut betting pool.</p>
          </div>

          <div className="flex justify-between mb-8">
            {[
              { num: 1, label: "Token", icon: Search },
              { num: 2, label: "Verify", icon: Check },
              { num: 3, label: "Configure", icon: Coins },
              { num: 4, label: "Review", icon: CheckCircle2 },
            ].map(({ num, label, icon: Icon }) => (
              <div key={num} className="flex items-center gap-2">
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all",
                  step >= num 
                    ? "bg-primary text-black" 
                    : "bg-white/10 text-white/50"
                )}>
                  {step > num ? <Check className="w-4 h-4" /> : num}
                </div>
                <span className={cn(
                  "text-xs font-tech uppercase tracking-wider hidden sm:inline",
                  step >= num ? "text-primary" : "text-white/50"
                )}>{label}</span>
              </div>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div 
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="space-y-2">
                  <Label className="text-[10px] font-tech uppercase tracking-widest text-muted-foreground">
                    SPL Token Mint Address
                  </Label>
                  <div className="relative">
                    <Input 
                      placeholder="Enter token mint address..."
                      value={mintAddress}
                      onChange={(e) => setMintAddress(e.target.value)}
                      onKeyDown={handleKeyDown}
                      className="bg-black/50 border-white/10 h-12 pr-12 font-mono text-sm focus:border-primary/50"
                      data-testid="input-mint-address"
                    />
                    <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Supports any SPL token including pump.fun tokens. Fetches metadata via Helius.
                  </p>
                </div>

                <Button 
                  onClick={handleFetchToken}
                  disabled={!mintAddress || isFetching}
                  className="w-full h-12 bg-primary text-black hover:bg-white font-bold"
                  data-testid="button-fetch-token"
                >
                  {isFetching ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Fetching Token Data...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Fetch Token Info
                    </>
                  )}
                </Button>
              </motion.div>
            )}

            {step === 2 && tokenInfo && (
              <motion.div 
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="flex items-center gap-4 p-4 bg-black/30 rounded-lg border border-white/5">
                  {tokenInfo.logoUrl ? (
                    <img src={tokenInfo.logoUrl} alt={tokenInfo.symbol} className="w-16 h-16 rounded-full" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                      <Coins className="w-8 h-8 text-primary" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white">{tokenInfo.name}</h3>
                    <p className="text-primary font-mono">${tokenInfo.symbol}</p>
                    <p className="text-xs text-muted-foreground mt-1">Decimals: {tokenInfo.decimals}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {priceData ? (
                      <div className="space-y-1">
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 flex items-center justify-end gap-1">
                          Current Price
                          {priceData.priceDiscrepancy && <AlertTriangle className="w-3 h-3 text-amber-400" />}
                        </div>
                        <div className="text-lg font-mono font-bold text-primary">
                          ${priceData.priceUsd.toFixed(Math.max(3, -Math.floor(Math.log10(priceData.priceUsd)) + 2))}
                        </div>
                        <div className="text-[9px] text-muted-foreground/60">via {priceData.source}</div>
                      </div>
                    ) : (
                      <div className="text-xs text-amber-400">⚠ Price unavailable</div>
                    )}
                  </div>
                </div>

                {/* Price Discrepancy Warning */}
                {priceData?.priceDiscrepancy && (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-[10px] text-amber-400 font-tech uppercase tracking-tighter flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Price varies across sources - using {priceData.source} data
                    </p>
                    <p className="text-[9px] text-amber-400/70 mt-1">
                      Multiple price sources returned different values. Using highest liquidity source for accuracy.
                    </p>
                  </div>
                )}

                {/* Market Cap and Liquidity */}
                {priceData && (priceData.marketCapUsd || priceData.liquidityUsd) && (
                  <div className="grid grid-cols-2 gap-3">
                    {priceData.marketCapUsd && (
                      <div className="p-3 rounded-lg bg-background/30 border border-white/10">
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Market Cap</div>
                        <div className="text-lg font-mono font-bold text-foreground">
                          {priceData.marketCapUsd >= 1000000
                            ? `$${(priceData.marketCapUsd / 1000000).toFixed(2)}M`
                            : priceData.marketCapUsd >= 1000
                            ? `$${(priceData.marketCapUsd / 1000).toFixed(2)}K`
                            : `$${priceData.marketCapUsd.toFixed(2)}`}
                        </div>
                      </div>
                    )}
                    {priceData.liquidityUsd && (
                      <div className="p-3 rounded-lg bg-background/30 border border-white/10">
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Liquidity</div>
                        <div className="text-lg font-mono font-bold text-foreground">
                          {priceData.liquidityUsd >= 1000000
                            ? `$${(priceData.liquidityUsd / 1000000).toFixed(2)}M`
                            : priceData.liquidityUsd >= 1000
                            ? `$${(priceData.liquidityUsd / 1000).toFixed(2)}K`
                            : `$${priceData.liquidityUsd.toFixed(2)}`}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* AI Safety Analysis */}
                {!showAnalysis ? (
                  <Button
                    onClick={handleAnalyzeToken}
                    disabled={isAnalyzing}
                    className="w-full h-12 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 hover:from-cyan-500/30 hover:to-blue-500/30 border border-cyan-500/30 text-black font-bold"
                  >
                    {isAnalyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing with AI Agent...
                      </>
                    ) : (
                      <>
                        <FlaskConical className="w-4 h-4 mr-2" />
                        Analyze Token Safety with AI
                      </>
                    )}
                  </Button>
                ) : isAnalyzing && !aiAnalysis ? (
                  <div className="p-6 rounded-lg border-2 border-cyan-500/30 bg-cyan-500/5 space-y-4">
                    {/* Loading Card - Shows during 30-60s analysis */}
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                      <span className="text-sm font-bold uppercase tracking-wide text-cyan-400">AI Analysis in Progress</span>
                    </div>

                    <div className="space-y-2 text-sm text-gray-400">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse" />
                        <span>Analyzing 1000+ transactions...</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                        <span>Detecting wallet clustering & Sybil attacks...</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                        <span>Checking pump.fun rug patterns...</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse" style={{ animationDelay: '0.6s' }} />
                        <span>Running Claude AI synthesis...</span>
                      </div>
                    </div>

                    <div className="text-xs text-gray-500 italic">
                      ⏱️ This typically takes 30-60 seconds for deep analysis
                    </div>
                  </div>
                ) : aiAnalysis ? (
                  <div className={cn(
                    "p-4 rounded-lg border-2 space-y-4",
                    aiAnalysis.riskLevel === 'critical' && "bg-red-500/10 border-red-500/50",
                    aiAnalysis.riskLevel === 'high' && "bg-orange-500/10 border-orange-500/50",
                    aiAnalysis.riskLevel === 'medium' && "bg-yellow-500/10 border-yellow-500/50",
                    aiAnalysis.riskLevel === 'low' && "bg-green-500/10 border-green-500/50"
                  )}>
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FlaskConical className={cn(
                          "w-5 h-5",
                          aiAnalysis.riskLevel === 'critical' && "text-red-400",
                          aiAnalysis.riskLevel === 'high' && "text-orange-400",
                          aiAnalysis.riskLevel === 'medium' && "text-yellow-400",
                          aiAnalysis.riskLevel === 'low' && "text-green-400"
                        )} />
                        <span className="text-sm font-bold uppercase tracking-wide">AI Safety Analysis</span>
                      </div>
                      <Badge className={cn(
                        "text-xs font-mono",
                        aiAnalysis.riskLevel === 'critical' && "bg-red-500/20 text-red-400 border-red-500/50",
                        aiAnalysis.riskLevel === 'high' && "bg-orange-500/20 text-orange-400 border-orange-500/50",
                        aiAnalysis.riskLevel === 'medium' && "bg-yellow-500/20 text-yellow-400 border-yellow-500/50",
                        aiAnalysis.riskLevel === 'low' && "bg-green-500/20 text-green-400 border-green-500/50"
                      )}>
                        {aiAnalysis.riskLevel?.toUpperCase() || 'UNKNOWN'}
                      </Badge>
                    </div>

                    {/* Safe Score */}
                    <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                      <span className="text-sm font-medium">Safety Score</span>
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-32 bg-black/50 rounded-full overflow-hidden">
                          <div 
                            className={cn(
                              "h-full transition-all",
                              aiAnalysis.safeScore >= 70 && "bg-green-500",
                              aiAnalysis.safeScore >= 50 && aiAnalysis.safeScore < 70 && "bg-yellow-500",
                              aiAnalysis.safeScore >= 30 && aiAnalysis.safeScore < 50 && "bg-orange-500",
                              aiAnalysis.safeScore < 30 && "bg-red-500"
                            )}
                            style={{ width: `${Math.min(100, Math.max(0, aiAnalysis.safeScore || 0))}%` }}
                          />
                        </div>
                        <span className={cn(
                          "text-lg font-mono font-bold",
                          aiAnalysis.safeScore >= 70 && "text-green-400",
                          aiAnalysis.safeScore >= 50 && aiAnalysis.safeScore < 70 && "text-yellow-400",
                          aiAnalysis.safeScore >= 30 && aiAnalysis.safeScore < 50 && "text-orange-400",
                          aiAnalysis.safeScore < 30 && "text-red-400"
                        )}>
                          {aiAnalysis.safeScore?.toFixed(1) || '0.0'}
                        </span>
                      </div>
                    </div>

                    {/* Recommendation */}
                    <div className="p-3 bg-black/30 rounded-lg">
                      <div className="text-xs font-medium text-muted-foreground mb-1">Pattern Detection Verdict</div>
                      <p className="text-sm leading-relaxed">{aiAnalysis.recommendation}</p>
                    </div>

                    {/* Claude AI Recommendation (if available) */}
                    {aiAnalysis.aiRecommendation && (
                      <div className="p-3 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center">
                              <span className="text-xs font-bold text-purple-400">AI</span>
                            </div>
                            <span className="text-xs font-medium text-purple-300">Claude's Analysis</span>
                          </div>
                          {aiAnalysis.aiConfidence && (
                            <Badge className="text-xs bg-purple-500/20 text-purple-300 border-purple-500/50">
                              {aiAnalysis.aiConfidence}% confident
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm leading-relaxed text-purple-100">{aiAnalysis.aiRecommendation}</p>
                      </div>
                    )}

                    {/* Key Reasons */}
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-muted-foreground">Detected Issues</div>
                      {aiAnalysis.reasons.slice(0, 5).map((reason: string, idx: number) => (
                        <div key={idx} className="flex items-start gap-2 text-xs p-2 bg-black/20 rounded">
                          {reason.startsWith('❌') ? (
                            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                          ) : reason.startsWith('⚠️') ? (
                            <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                          ) : (
                            <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                          )}
                          <span className="leading-relaxed">{reason.replace(/^[❌⚠️✓]\s*/, '')}</span>
                        </div>
                      ))}
                    </div>

                    {/* Metrics Summary */}
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/5">
                      <div className="p-2 bg-black/20 rounded text-xs">
                        <div className="text-muted-foreground mb-1">Whale Concentration</div>
                        <div className="font-mono font-bold">{aiAnalysis.metrics.whaleConcentration?.toFixed(1) || '0.0'}%</div>
                      </div>
                      <div className="p-2 bg-black/20 rounded text-xs">
                        <div className="text-muted-foreground mb-1">Holders</div>
                        <div className="font-mono font-bold">{aiAnalysis.metrics.holderCount || 0}</div>
                      </div>
                      <div className="p-2 bg-black/20 rounded text-xs">
                        <div className="text-muted-foreground mb-1">Token Age</div>
                        <div className="font-mono font-bold">{aiAnalysis.metrics.tokenAgeHours?.toFixed(1) || '0.0'}h</div>
                      </div>
                      <div className="p-2 bg-black/20 rounded text-xs">
                        <div className="text-muted-foreground mb-1">Transactions</div>
                        <div className="font-mono font-bold">{aiAnalysis.metrics.transactionCount || 0}</div>
                      </div>
                    </div>

                    {/* Re-analyze button */}
                    <Button
                      onClick={handleAnalyzeToken}
                      disabled={isAnalyzing}
                      variant="ghost"
                      className="w-full h-8 text-xs"
                    >
                      {isAnalyzing ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                          Re-analyzing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-3 h-3 mr-2" />
                          Re-analyze Token
                        </>
                      )}
                    </Button>
                  </div>
                ) : null}

                {!tokenInfo.metadataFound && (
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <p className="text-[10px] text-yellow-400 font-tech uppercase tracking-tighter flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> Token metadata not found. Using fallback values.
                    </p>
                  </div>
                )}

                {/* Jupiter Swap disabled due to DNS resolution issues */}
                {/* Users can purchase tokens directly from Jupiter.ag or other DEXs */}

                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setStep(1);
                      setTokenInfo(null);
                      setPriceUsd(null);
                      setSolAmount("");
                      setJupiterQuote(null);
                    }}
                    className="flex-1 h-12 border-white/10"
                    data-testid="button-back-step1"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                  <Button 
                    onClick={() => setStep(3)}
                    disabled={!canProceedToStep3}
                    className="flex-1 h-12 bg-primary text-black hover:bg-white font-bold"
                    data-testid="button-proceed-step3"
                  >
                    Configure Pool <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}

            {step === 3 && tokenInfo && (
              <motion.div 
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label className="text-[10px] font-tech uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                        <Users className="w-3 h-3" /> Max Participants (2-{isFreePool ? '10' : '20'})
                      </Label>
                      <span className="text-primary font-mono font-bold">{participants}</span>
                    </div>
                    <Slider
                      value={[participants]}
                      onValueChange={([v]) => setParticipants(v)}
                      min={2}
                      max={isFreePool ? 10 : 20}
                      step={1}
                      className="py-4"
                      data-testid="slider-participants"
                    />
                    {isFreePool && (
                      <p className="text-[10px] text-green-400 font-tech uppercase tracking-tighter">
                        Free pools limited to 10 participants (1 creator + 9 free joins)
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[10px] font-tech uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                      <Coins className="w-3 h-3" /> Entry Amount ({tokenInfo.symbol})
                    </Label>
                    <div className="relative">
                      <Input 
                        type="number"
                        placeholder="0.00"
                        value={entryAmount}
                        onChange={(e) => setEntryAmount(e.target.value)}
                        className="bg-black/50 border-white/10 h-12 text-xl font-mono focus:border-primary/50"
                        data-testid="input-entry-amount"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-muted-foreground">
                        {priceData?.priceUsd ? `≈ $${entryUsd.toFixed(2)}` : "Price Unknown"}
                      </div>
                    </div>

                    {/* USD Value Display & Validation */}
                    {priceData?.priceUsd && entryAmount && parseFloat(entryAmount) > 0 && (
                      <div className={`p-3 rounded-lg border mt-2 ${entryUsd >= 5 ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-tech uppercase tracking-widest text-muted-foreground">USD Value:</span>
                          <span className={`text-lg font-mono font-bold ${entryUsd >= 5 ? 'text-green-500' : 'text-red-500'}`}>
                            ${entryUsd.toFixed(2)}
                          </span>
                        </div>
                        {entryUsd < 5 && (
                          <div className="mt-2 space-y-2">
                            <p className="text-[10px] text-red-400 font-tech uppercase tracking-tighter flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> Minimum entry is $5.00 USD
                            </p>
                            <p className="text-[10px] text-red-400 font-mono">
                              Suggested: {suggestedAmount.toFixed(tokenInfo.decimals)} {tokenInfo.symbol}
                            </p>
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full h-7 text-[9px] border-red-500/30 hover:bg-red-500/20 font-black uppercase tracking-widest text-red-400"
                              onClick={() => setEntryAmount(suggestedAmount.toString())}
                              data-testid="button-use-suggested"
                            >
                              Use Suggested Amount
                            </Button>
                          </div>
                        )}
                        {entryUsd >= 5 && (
                          <div className="mt-1 text-[10px] text-green-500">
                            ✓ Amount meets minimum requirement
                          </div>
                        )}
                      </div>
                    )}

                    {/* Warning when price unavailable */}
                    {!priceData?.priceUsd && (
                      <div className="mt-2 p-3 rounded-lg border bg-amber-500/10 border-amber-500/30">
                        <div className="text-[10px] text-amber-400 font-tech uppercase flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> Price unavailable - unable to enforce $5 minimum
                        </div>
                        <div className="mt-1 text-[9px] text-amber-400/80">
                          Proceed with caution. Minimum $5 USD equivalent recommended.
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[10px] font-tech uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                      <Clock className="w-3 h-3" /> Event Horizon (Lock Duration)
                    </Label>
                    <div className="grid grid-cols-5 gap-2">
                      {[1, 5, 15, 30, 60].map((m) => (
                        <Button
                          key={m}
                          size="sm"
                          variant="outline"
                          onClick={() => setLockDuration(m)}
                          className={cn(
                            "h-8 text-[10px] font-mono border-white/5",
                            lockDuration === m && "bg-primary/20 border-primary text-primary"
                          )}
                          data-testid={`button-duration-${m}`}
                        >
                          {m}m
                        </Button>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 pt-2">
                      <Input
                        type="number"
                        placeholder="Custom (min)"
                        value={customDuration}
                        onChange={(e) => {
                          setCustomDuration(e.target.value);
                          const val = parseInt(e.target.value);
                          if (val > 0) setLockDuration(val);
                        }}
                        className="bg-black/50 border-white/10 h-8 text-xs font-mono"
                        data-testid="input-custom-duration"
                      />
                    </div>
                  </div>

                  {/* FREE Pool Toggle (only for sponsor wallet) */}
                  {address?.toLowerCase() === SPONSOR_WALLET_PUBKEY.toLowerCase() && (
                    <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label htmlFor="free-pool" className="text-[10px] font-tech uppercase tracking-widest text-white flex items-center gap-2">
                            <FlaskConical className="w-4 h-4 text-green-400" />
                            FREE Pool (Gasless Join)
                          </Label>
                          <p className="text-[9px] text-muted-foreground">
                            Users join without paying. You sponsor all entries. Max 10 participants (1 creator + 9 free).
                          </p>
                        </div>
                        <Switch
                          id="free-pool"
                          checked={isFreePool}
                          onCheckedChange={(checked) => {
                            setIsFreePool(checked);
                            if (checked && participants > 10) {
                              setParticipants(10);
                            }
                          }}
                          data-testid="switch-free-pool"
                        />
                      </div>
                      {isFreePool && (
                        <div className="text-[9px] text-green-400 space-y-1 font-tech uppercase tracking-tighter">
                          <p>✓ Backend will handle on-chain joins</p>
                          <p>✓ Max 10 participants (1 creator + 9 free joins)</p>
                          <p>✓ Winners receive rewards to their real wallet</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => setStep(2)}
                    className="flex-1 h-12 border-white/10 hover:bg-white/5 uppercase font-black text-xs tracking-widest"
                    data-testid="button-back-step2"
                  >
                    Back
                  </Button>
                  <Button 
                    disabled={!canProceedToStep4}
                    onClick={() => setStep(4)}
                    className="flex-[2] h-12 bg-primary text-black font-black uppercase tracking-widest hover:bg-white transition-all disabled:opacity-50"
                    data-testid="button-proceed-step4"
                  >
                    Review <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            )}

            {step === 4 && tokenInfo && (
              <motion.div 
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="bg-black/40 border border-white/10 rounded-xl divide-y divide-white/5 overflow-hidden">
                  <div className="p-4 flex justify-between items-center bg-primary/5">
                    <div className="text-[10px] font-tech text-muted-foreground uppercase">Token</div>
                    <div className="text-right font-display font-black text-sm uppercase italic">
                      {tokenInfo.name} <span className="text-primary">({tokenInfo.symbol})</span>
                    </div>
                  </div>
                  <div className="p-4 flex justify-between items-center">
                    <div className="text-[10px] font-tech text-muted-foreground uppercase tracking-widest">Participants</div>
                    <div className="font-mono text-sm font-bold">{participants} Max</div>
                  </div>
                  <div className="p-4 flex justify-between items-center">
                    <div className="text-[10px] font-tech text-muted-foreground uppercase tracking-widest">Entry</div>
                    <div className="text-right">
                      <div className="font-mono text-sm font-black">{entryAmount} {tokenInfo.symbol}</div>
                      <div className="text-[10px] text-primary/70 font-mono">
                        {priceData?.priceUsd ? `≈ $${entryUsd.toFixed(2)}` : "Price Unknown"}
                      </div>
                    </div>
                  </div>
                  <div className="p-4 flex justify-between items-center">
                    <div className="text-[10px] font-tech text-muted-foreground uppercase tracking-widest">Horizon</div>
                    <div className="font-mono text-sm font-bold">{lockDuration} Minutes</div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => setStep(3)}
                    className="flex-1 h-12 border-white/10 hover:bg-white/5 uppercase font-black text-xs tracking-widest"
                    data-testid="button-back-step3"
                  >
                    Back
                  </Button>
                  {isConnected ? (
                    <Button 
                      onClick={handleCreate}
                      disabled={isCreating}
                      className="flex-[2] h-12 bg-primary text-black font-black uppercase tracking-widest hover:bg-white transition-all shadow-[0_0_20px_rgba(0,240,255,0.3)]"
                      data-testid="button-create-pool"
                    >
                      {isCreating ? (
                        <Loader2 className="animate-spin" />
                      ) : (
                        <>
                          Initialize Black Hole
                          <Zap className="ml-2 w-4 h-4 fill-current" />
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button 
                      onClick={connect}
                      className="flex-[2] h-12 bg-white/10 text-white hover:bg-white/20 border border-white/10 font-bold"
                      data-testid="button-connect-wallet"
                    >
                      Connect Wallet to Initialize
                    </Button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
